import * as React from 'react';
import FormControl from '@mui/material/FormControl';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { Step, StepContent, StepLabel, Stepper } from '@mui/material';
import { DashboardChartT } from 'business/definitions';
import { Alert } from 'lib/alert/models';
import AlertTypeStep from './AlertTypeStep';
import { AlertFormContext } from '../Contexts/AlertFormContext';
import AlertThresholdStep from './AlertThresholdStep';
import AlertVentilation from './AlertVentilation';
import AlertPerimeters from './AlertPerimeters';
import AlertPersonalization from './AlertPersonalization';
import AlertSharings from './AlertSharings';
import { getCompletedStepLabel } from './helpers';
import { useModalContext } from '../Contexts/ModalContext';
import AlertFormProvider from 'components/ChartAlert/Contexts/AlertFormProvider';
import AlertSentenceProvider from 'components/ChartAlert/Contexts/AlertSentenceProvider';

const StyDrawerContentText = styled(Typography, {
    name: 'StyDrawerContentText',
})({
    fontWeight: 400,
    color: '#666',
    fontSize: '1rem',
    lineHeight: '1.5rem',
    padding: '1rem 0',
});

const StyTitle = styled(Typography, {
    name: 'StyTitle',
})({
    display: 'inline',
});

type Props = {
    chart: DashboardChartT;
    chartTitle: string;
};

const AlertForm = ({ chart, chartTitle }: Props) => {
    const { setDirty } = useModalContext();
    const AlertSteps: ReadonlyArray<JSX.Element> = [
        <AlertTypeStep key="type-step" />,
        <AlertThresholdStep chart={chart} key="threshold-step" />,
        <AlertVentilation key="ventilation-step" />,
        <AlertPerimeters chart={chart} key="perimeters-step" />,
        <AlertPersonalization key="personalization-step" />,
        <AlertSharings chartKey={chart.key} key="sharings-step" />,
    ];

    const { t } = useTranslation();

    const getOptionalLabel = (
        alert: Partial<Alert>,
        activeStep: number,
        step: number,
    ): string | null => {
        if (activeStep < step) {
            return null;
        }
        if (activeStep === step) {
            return t(`alert.create.steps.${step}.description`);
        }
        return getCompletedStepLabel(step, alert, t);
    };

    return (
        <>
            <StyTitle variant="h5">{chartTitle}</StyTitle>
            <FormControl sx={{ position: 'relative', height: '100%' }}>
                <StyDrawerContentText>
                    {t('alert.create.description')}
                </StyDrawerContentText>

                <AlertFormProvider setHasChanges={setDirty}>
                    <AlertSentenceProvider>
                        <AlertFormContext.Consumer>
                            {({ alert, activeStep }) => (
                                <Stepper
                                    activeStep={activeStep}
                                    orientation="vertical"
                                >
                                    {AlertSteps.map((AlertStep, index) => (
                                        <Step key={AlertStep.key}>
                                            <StepLabel
                                                optional={getOptionalLabel(
                                                    alert,
                                                    activeStep,
                                                    index,
                                                )}
                                            >
                                                {t(
                                                    `alert.create.steps.${index}.title`,
                                                )}
                                            </StepLabel>
                                            <StepContent
                                                className={`pendo-create-alert-step-${index}`}
                                            >
                                                {AlertStep}
                                            </StepContent>
                                        </Step>
                                    ))}
                                </Stepper>
                            )}
                        </AlertFormContext.Consumer>
                    </AlertSentenceProvider>
                </AlertFormProvider>
            </FormControl>
        </>
    );
};

export default AlertForm;
